import axios from 'axios';
import api from './api';

const API_URL = process.env.REACT_APP_FRONT_API_URL;
const SPOTIFY_API = 'https://api.spotify.com/v1';

export interface SpotifyUser {
  id: string;
  display_name: string;
  email: string;
  images: Array<{
    url: string;
    height: number;
    width: number;
  }>;
  country: string;
  product: string;
  type: string;
}

interface TokenCheckResponse {
  valid: boolean;
  user?: SpotifyUser;
  refreshed?: boolean;
  token?: string;
  spotify?: string;
  expires_in?: number;
  error?: string;
}

// Interfaces pour les différents types de contenu Spotify
interface SpotifyImage {
  url: string;
  height: number;
  width: number;
}

interface SpotifyArtist {
  id: string;
  name: string;
  images: SpotifyImage[];
  genres: string[];
}

interface SpotifyAlbum {
  id: string;
  name: string;
  images: SpotifyImage[];
  artists: SpotifyArtist[];
  release_date: string;
}

interface SpotifyTrack {
  id: string;
  name: string;
  album: SpotifyAlbum;
  artists: SpotifyArtist[];
  duration_ms: number;
  external_urls: {
    spotify: string;
  };
}

interface SpotifyPlaylist {
  id: string;
  name: string;
  images: SpotifyImage[];
}

interface SpotifyDevice {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  is_private_session: boolean;
  is_restricted: boolean;
  volume_percent: number;
}

export interface SpotifyContent {
  type: 'track' | 'album' | 'artist' | 'playlist' | null;
  data: SpotifyTrack | SpotifyAlbum | SpotifyArtist | SpotifyPlaylist | null;
  coverUrl: string;
}

class SpotifyService {
  private static async apiRequest<T>(endpoint: string, token: string): Promise<T> {
    try {
      const response = await axios.get(`${SPOTIFY_API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la requête API Spotify (${endpoint}):`, error);
      throw new Error(`Échec de la requête Spotify: ${endpoint}`);
    }
  }

  static async fetchService(url: string, retryOnError: boolean = true): Promise<any> {
    const accessToken = localStorage.getItem('spotify_access');
    if (!accessToken) {
      return null;
    }
    try {
      return await this.apiRequest<any>(url, accessToken);
    } catch (error) {
      if (retryOnError) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          return await this.fetchService(url, false);
        }
      }
      console.error('Erreur lors de la récupération des informations Spotify:', error);
      throw new Error('Impossible de récupérer les informations Spotify');
    }
  }

  private static async batchApiRequest<T>(
    endpoint: string,
    ids: string[],
    limit: number
  ): Promise<T[]> {
    if (!ids.length) return [];

    const results: T[] = [];

    for (let i = 0; i < ids.length; i += limit) {
      const idsChunk = ids.slice(i, i + limit).join(',');
      const response = await this.fetchService(`${endpoint}?ids=${idsChunk}`, false);
      results.push(...response[endpoint.slice(1)]);
    }

    return results;
  }

  // Redirige l'utilisateur vers la page d'authentification Spotify
  static initiateLogin(platform: 'web' | 'mobile' = 'web'): void {
    window.location.href = `${API_URL}/spotify/login?platform=${platform}`;
  }

  // Vérifie si le token est valide
  static async checkToken(access_token: string): Promise<TokenCheckResponse> {
    try {
      const response = await api.post(`${API_URL}/spotify/check-token`, {
        access_token,
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      return { valid: false, error: 'Erreur lors de la vérification du token' };
    }
  }

  static async refreshToken(): Promise<boolean> {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return false;
    }

    const spotifyResponse = await this.checkToken(accessToken);
    if (spotifyResponse.valid && spotifyResponse.user) {
      if (spotifyResponse.token && spotifyResponse.spotify) {
        localStorage.setItem('access_token', spotifyResponse.token);
        localStorage.setItem('spotify_access', spotifyResponse.spotify);
      }
      return true;
    }
    return false;
  }

  // Récupère les informations de l'utilisateur
  static async getUserInfo(retryOnError: boolean = true): Promise<SpotifyUser | null> {
    return this.fetchService('/me', retryOnError);
  }

  // Récupère les informations d'une piste Spotify
  static async getTrackInfo(id: string): Promise<SpotifyTrack> {
    return this.fetchService(`/tracks/${id}`);
  }

  // Récupère les informations de plusieurs pistes Spotify en une seule requête
  static async getMultipleTracksInfo(ids: string[]): Promise<SpotifyTrack[]> {
    return this.batchApiRequest<SpotifyTrack>('/tracks', ids, 50);
  }

  // Récupère les informations d'un album Spotify
  static async getAlbumInfo(id: string): Promise<SpotifyAlbum> {
    return this.fetchService(`/albums/${id}`);
  }

  // Récupère les informations de plusieurs albums Spotify en une seule requête
  static async getMultipleAlbumsInfo(ids: string[]): Promise<SpotifyAlbum[]> {
    return this.batchApiRequest<SpotifyAlbum>('/albums', ids, 20);
  }

  // Récupère les informations d'un artiste Spotify
  static async getArtistInfo(id: string): Promise<SpotifyArtist> {
    return this.fetchService(`/artists/${id}`);
  }

  // Récupère les informations de plusieurs artistes Spotify en une seule requête
  static async getMultipleArtistsInfo(ids: string[]): Promise<SpotifyArtist[]> {
    return this.batchApiRequest<SpotifyArtist>('/artists', ids, 50);
  }

  static async getPlaylistInfo(id: string): Promise<SpotifyPlaylist> {
    return this.fetchService(`/playlists/${id}`);
  }

  // Récupère les informations d'un contenu Spotify à partir de son URL
  static async getSpotifyContent(url: string): Promise<SpotifyContent | null> {
    try {
      // Vérifier si l'URL est au format type:id
      if (!url || !url.includes(':')) return null;

      const [type, id] = url.split(':');
      if (!type || !id) return null;

      let data;
      let coverUrl = '';

      switch (type) {
        case 'track':
          data = await this.getTrackInfo(id);
          coverUrl = data.album.images[0]?.url || '';
          break;
        case 'album':
          data = await this.getAlbumInfo(id);
          coverUrl = data.images[0]?.url || '';
          break;
        case 'artist':
          data = await this.getArtistInfo(id);
          coverUrl = data.images[0]?.url || '';
          break;
        case 'playlist':
          data = await this.getPlaylistInfo(id);
          coverUrl = data.images[0]?.url || '';
          break;
        default:
          return null;
      }

      return {
        type: type as 'track' | 'album' | 'artist' | 'playlist',
        data,
        coverUrl,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des informations Spotify:', error);
      return null;
    }
  }

  // Récupère la liste des appareils Spotify disponibles
  static async getAvailableDevices(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await this.fetchService('/me/player/devices');

      // Vérifier si la réponse est valide
      if (!response || !response.devices) {
        console.warn('Aucun appareil Spotify disponible');
        return [];
      }

      const devices = response.devices as SpotifyDevice[];

      // Filtrer les appareils actifs et non restreints
      return devices
        .filter((device) => !device.is_restricted)
        .map((device) => ({
          id: device.id,
          name: device.name,
        }));
    } catch (error) {
      console.error('Erreur lors de la récupération des appareils Spotify:', error);
      // Retourner un tableau vide en cas d'erreur plutôt que de lever une exception
      return [];
    }
  }

  // Récupère les informations de plusieurs contenus Spotify en minimisant les appels API
  static async getMultipleSpotifyContents(urls: string[]): Promise<Record<string, string>> {
    try {
      const contentMap = new Map<string, { type: string; id: string; index: number }>();
      const trackIds: string[] = [];
      const albumIds: string[] = [];
      const artistIds: string[] = [];
      const playlistIds: string[] = [];
      const results: Record<string, string> = {};

      urls.forEach((url, index) => {
        if (!url || !url.includes(':')) return;

        const [type, id] = url.split(':');
        if (!type || !id) return;

        contentMap.set(`${type}:${id}`, { type, id, index });

        switch (type) {
          case 'track':
            trackIds.push(id);
            break;
          case 'album':
            albumIds.push(id);
            break;
          case 'artist':
            artistIds.push(id);
            break;
          case 'playlist':
            playlistIds.push(id);
            break;
        }
      });

      const [tracks, albums, artists] = await Promise.all([
        trackIds.length ? this.getMultipleTracksInfo(trackIds) : [],
        albumIds.length ? this.getMultipleAlbumsInfo(albumIds) : [],
        artistIds.length ? this.getMultipleArtistsInfo(artistIds) : [],
      ]);

      const playlists = await Promise.all(playlistIds.map((id) => this.getPlaylistInfo(id)));

      // Construire les maps pour un accès rapide
      const dataMap = new Map();
      tracks.forEach((item) => dataMap.set(`track:${item.id}`, item.album.images[0]?.url || ''));
      albums.forEach((item) => dataMap.set(`album:${item.id}`, item.images[0]?.url || ''));
      artists.forEach((item) => dataMap.set(`artist:${item.id}`, item.images[0]?.url || ''));
      playlists.forEach((item) => dataMap.set(`playlist:${item.id}`, item.images[0]?.url || ''));

      // Remplir les résultats
      contentMap.forEach(({ type, id, index }) => {
        const key = `${type}:${id}`;
        const item = dataMap.get(key);
        if (item) {
          results[index] = item;
        }
      });

      return results;
    } catch (error) {
      console.error('Erreur lors de la récupération des informations Spotify multiples:', error);
      return urls.reduce((acc, url) => {
        acc[url] = '';
        return acc;
      }, {} as Record<string, string>);
    }
  }

  // Vérifie si l'utilisateur est connecté à Spotify
  static isConnectedToSpotify(): boolean {
    // Vérifier si un token d'accès est disponible dans le localStorage
    const accessToken = localStorage.getItem('spotify_access');
    return !!accessToken;
  }

  // Récupère la miniature d'une piste Spotify à partir d'une URL au format type:id
  static async getThumbnailSpotify(url: string): Promise<string> {
    if (!url || !url.includes(':')) return '';

    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return '';
    }

    try {
      const response = await api.get(`${API_URL}/spotify/infos/${url}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 30000,
      });
      return response.data?.thumbnail_url || '';
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      return '';
    }
  }
}

export default SpotifyService;
